import React from 'react';

import { blanketInsuranceApis } from '@apis/next/blanketInsuranceApis';
import { Button } from '@components/Button';
import { Layout } from '@components/Layout';
import { GridForm } from '@components/NextForms/GridForm';
import { SortButtons } from '@components/sort/SortButtons';
import { BlanketInsuranceCard } from '@components/utilities/BlanketInsurance/BlanketInsuranceCard';
import { Container } from '@components/utilities/Contact/Container';
import { VerticalSpace } from '@components/VerticalSpace';
import { instructionStrings } from '@data/instruction';
import { useSearch } from '@hooks/next/useSearch';
import { BlanketInsuranceSearchQuery } from '@models/next/BlanketInsurance';
import { empty } from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles, useIsMobile } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import clsx from 'clsx';
import { Link } from 'react-router-dom';

const strings = createStrings({
  ja: {
    add: '新規登録',
    noData: () => (
      <>
        まだ{instructionStrings.insuranceTags.BLANKET}を登録していません。
        <br />
        <span>
          <Link to={url('/utilities/Commodity/AddCommodity')}>「新規登録」</Link>
          をクリックして商品を登録してください。
        </span>
      </>
    ),
    noSearchResult: () => (
      <>
        検索条件に当てはまるデータが見つかりませんでした。
        <br />
        検索条件を変えて検索してみてください。
      </>
    ),
    sortByCompany: '保険会社順',
    sortByCreatedAtAsc: '登録順',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  body: {
    ...common.flexColumn,
    flex: 1,
  },
  blanketInsuranceGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gridAutoRows: '140px',
    gap: '1rem',
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  noResultHint: {
    ...common.textGray500,
    ...common.middleText,
    ...common.textAlignCenter,
    margin: '200px 1rem',
    [theme.breakpoints.down('xs')]: {
      margin: '1rem',
    },
    ['& a']: {
      ...common.link,
    },
  },
}));

const BlanketInsuranceSearch: React.FC = () => {
  const { search, updateSearch } = useSearch(BlanketInsuranceSearchQuery);

  const isMobile = useIsMobile();

  const form = useForm(BlanketInsuranceSearchQuery, {
    initialOutput: search,
    enableReinitialize: true,
  });

  const { fields } = form.root;

  return (
    <form {...form.bindForm({ onSubmit: (v) => updateSearch({ ...v, page: 1 }) })}>
      <GridForm
        columns={isMobile ? 1 : 4}
        fields={[fields.company, fields.policy_no]}
        buttons={
          <>
            <Button
              color="primary"
              size="small"
              type="submit"
              text={formStrings.search}
              style={{ width: 88 }}
            />
            <Button
              color="primary"
              size="small"
              type="submit"
              text={formStrings.reset}
              onClick={() => form.root.setOutput(empty)}
              style={{ width: 88 }}
            />
          </>
        }
      />
    </form>
  );
};

export const BlanketInsurances: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { search, updateSearch } = useSearch(BlanketInsuranceSearchQuery);

  const queries = {
    ...search,
    page_size: search.page_size ?? 10,
  } satisfies BlanketInsuranceSearchQuery;

  const blanketInsurancesQuery = blanketInsuranceApis.useGet('/blanket-insurance', {
    queries,
  });

  const isNoSearchResult = !!search.company || !!search.policy_no;

  return (
    <Layout
      loading={blanketInsurancesQuery.isLoading}
      errors={[blanketInsurancesQuery.error]}
      title={instructionStrings.blanketInsuranceList}
      breadcrumbs={['/utilities/Dashboard', '/utilities/BlanketInsurance/BlanketInsurances']}
    >
      <div className={classes.body}>
        <Container>
          <VerticalSpace height={16} />
          <div className={clsx(classes.pageTitleText, classes.textPrimary)}>
            {instructionStrings.blanketInsuranceList}
          </div>
          <VerticalSpace height={16} />

          <BlanketInsuranceSearch />
          <VerticalSpace height={16} />

          <div className={clsx(classes.flexInline, classes.justifyBetween)}>
            <Link to={url('/utilities/BlanketInsurance/AddBlanketInsurance')}>
              <Button text={strings.add} color="primary" size="small" style={{ width: 133 }} />
            </Link>

            <div className={classes.flex1} />

            <SortButtons
              currentSortKey={queries.ordering?.[0] ?? '-created_at'}
              items={[
                {
                  title: strings.sortByCompany,
                  sortKey: '-company',
                },
                {
                  title: strings.sortByCreatedAtAsc,
                  sortKey: '-created_at',
                },
              ]}
              onSortKeyChanged={(ordering) => updateSearch({ ordering: [ordering] })}
            />
          </div>

          <VerticalSpace height={30} />

          {!!blanketInsurancesQuery.data?.length && (
            <>
              <div className={classes.blanketInsuranceGrid}>
                {blanketInsurancesQuery.data?.map((item) => (
                  <BlanketInsuranceCard key={item.id} item={item} />
                ))}
              </div>
            </>
          )}

          {blanketInsurancesQuery.data?.length === 0 && (
            <div className={classes.noResultHint}>
              {isNoSearchResult ? strings.noSearchResult() : strings.noData()}
            </div>
          )}

          <VerticalSpace height={46} />
        </Container>
      </div>
    </Layout>
  );
};
